/**
 * Identifiers for the resumable, idempotent chunked commit (D5).
 *
 * Within one import *attempt*, a retry (or a resumed run after interruption) must
 * send the *same* per-chunk `correlation_id`, so the API — which derives the
 * batch `Idempotency-Key` from `correlation_id` (`PayComponentImportInput`) —
 * returns the cached response instead of double-committing mid-run (#892).
 *
 * A *new* attempt of the same file (after a prior successful import, or a fresh
 * commit from Review) must get a new run id so the server executes the batch
 * again and surfaces real row outcomes such as `PAYCOMP_ASSIGNMENT_EXISTS`
 * (#212). Resume reuses the run id persisted in sessionStorage for that attempt.
 */

/**
 * Stable, non-cryptographic 32-bit signature of a string (FNV-1a), returned as
 * zero-padded hex. Same input → same output across runs and reloads; it only
 * needs to be collision-resistant enough to distinguish one import file from
 * another (not a security hash).
 */
export function fileSignature(text: string): string {
  let hash = 0x811c9dc5; // FNV offset basis
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193); // FNV prime
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

/**
 * Stable fingerprint of company + environment + CSV content. Used to key
 * sessionStorage commit progress (`commitProgress.ts`), not as the server
 * idempotency run id.
 */
export function deriveRunId(
  companyCode: string,
  mypayEnvironment: string,
  csvText: string,
): string {
  return fileSignature(`${companyCode}\n${mypayEnvironment}\n${csvText}`);
}

/**
 * Unique run id for one import attempt. Persisted at commit start and reused
 * only when resuming that same interrupted attempt (D5 / #212).
 */
export function createRunId(): string {
  return crypto.randomUUID().replace(/-/g, "");
}

/**
 * Deterministic per-chunk correlation id: `pci-<runId>-c<chunkIndex>` (D5). The
 * chunk index is 0-based and stable for a given file partitioning, so the same
 * chunk always maps to the same correlation id.
 */
export function chunkCorrelationId(runId: string, chunkIndex: number): string {
  return `pci-${runId}-c${chunkIndex}`;
}
